import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useUsaNetworkStats = () => {
  return useQuery({
    queryKey: ['usa-network-stats'],
    queryFn: async () => {
      // Get the US Federal Government channel ID
      const { data: channelData } = await supabase
        .from('channels')
        .select('id')
        .eq('slug', 'us-federal-government')
        .single();

      if (!channelData) {
        throw new Error('US Federal Government channel not found');
      }

      const channelId = channelData.id;

      // Get total participants in the US Federal Government channel
      const { data: participantsData, error: participantsError } = await supabase
        .from('user_organizations')
        .select('user_id')
        .eq('channel_id', channelId);

      if (participantsError) throw participantsError;

      const totalParticipants = participantsData?.length || 0;
      const userIds = participantsData?.map(p => p.user_id) || [];

      // Get active contributors (users who have created content)
      const activeContributorIds = new Set<string>();

      // KB creators
      const { data: kbCreators } = await supabase
        .from('knowledge_bases')
        .select('creator_id')
        .in('creator_id', userIds);
      
      kbCreators?.forEach(kb => activeContributorIds.add(kb.creator_id));

      // Solution creators
      const { data: solutionCreators } = await supabase
        .from('solutions')
        .select('creator_id')
        .in('creator_id', userIds);
      
      solutionCreators?.forEach(s => activeContributorIds.add(s.creator_id));

      // Call creators
      const { data: callCreators } = await supabase
        .from('calls')
        .select('creator_id')
        .in('creator_id', userIds);
      
      callCreators?.forEach(c => activeContributorIds.add(c.creator_id));

      // Sprint creators
      const { data: sprintCreators } = await supabase
        .from('prompt_architecture_sprints')
        .select('creator_id')
        .in('creator_id', userIds);
      
      sprintCreators?.forEach(s => activeContributorIds.add(s.creator_id));

      // Inviters
      const { data: inviters } = await supabase
        .from('invitations')
        .select('inviter_id')
        .in('inviter_id', userIds);
      
      inviters?.forEach(i => activeContributorIds.add(i.inviter_id));

      const activeContributors = activeContributorIds.size;

      // Get published knowledge bases count for US Federal Government
      const { data: kbData, error: kbError } = await supabase
        .from('knowledge_bases')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'published')
        .eq('channel_name', 'US Federal Government');

      if (kbError) throw kbError;

      // Get published solutions count for US Federal Government
      const { data: solutionsData, error: solutionsError } = await supabase
        .from('solutions')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'published')
        .eq('channel_name', 'US Federal Government');

      if (solutionsError) throw solutionsError;

      // Get department distribution from user_expertise
      const { data: expertiseData, error: expertiseError } = await supabase
        .from('user_expertise')
        .select('domain_name, user_id')
        .in('user_id', userIds);

      if (expertiseError) throw expertiseError;

      // Count participants by domain (department)
      const departmentCounts = new Map<string, Set<string>>();
      
      expertiseData?.forEach((expertise) => {
        if (!departmentCounts.has(expertise.domain_name)) {
          departmentCounts.set(expertise.domain_name, new Set());
        }
        departmentCounts.get(expertise.domain_name)?.add(expertise.user_id);
      });

      // Convert to array and sort by participant count
      const departments = Array.from(departmentCounts.entries())
        .map(([name, userSet]) => ({
          name,
          participants: userSet.size
        }))
        .sort((a, b) => b.participants - a.participants)
        .slice(0, 5); // Top 5 departments

      return {
        totalParticipants,
        activeContributors,
        knowledgeBases: kbData?.length || 0,
        totalSolutions: solutionsData?.length || 0,
        departments
      };
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });
};
